import type {PaymentMethodCreateParams} from '@stripe/stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {FormFields, onCheckoutUpdate, onCheckoutSubmitManual, onCheckoutSubmitPaymentData, tokenizedMethodSelected, onCheckoutSubmitErrorManual, unblockCheckout} from '../checkout';
import {Feature} from '../features';
import {type StripePaymentData, type AbstractStripePayment} from './abstract/class-abstract-stripe-payment';
import {StripeCardPayment} from './class-card-payment';
import {StripeBancontactPayment} from './class-bancontact-payment';
import {StripeGiropayPayment} from './class-giropay-payment';
import {StripeAffirmPayment} from './class-affirm-payment';
import {StripeSofortPayment} from './class-sofort-payment';
import {StripeEpsPayment} from './class-eps-payment';
import {StripeP24Payment} from './class-p24-payment';
import {StripeSepaDebitPayment} from './class-sepadebit-payment';
import {StripeKlarnaPayment} from './class-klarna-payment';
import {StripeAfterPayPayment} from './class-afterpay-payment';
import {StripeIdealPayment} from './class-ideal-payment';
import {StripeGooglepayPayment} from './class-googlepay-payment';
import {StripeApplepayPayment} from './class-applepay-payment';
import {StripeAchdebitPayment} from './class-achdebit-payment';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {getErrorString} from '../../../@shared/ts/error';
import {insertCaptchaFields, cleanupCaptchaFields} from '../botProtection';
import {cleanupTransactionFields, createPaymentTransaction, updatePaymentTransaction, insertTransactionFields} from '../transaction';

async function initStripePayments($checkoutForm: HTMLFormElement): Promise<void> {
	if (!Feature.enabled(FeatureFlag.STRIPE_GATEWAYS)) {
		return;
	}

	const publicKey = Feature.metadata<string>(FeatureFlag.STRIPE_GATEWAYS, 'public_key');
	const connectId = Feature.metadata<string>(FeatureFlag.STRIPE_GATEWAYS, 'connect_id');

	if (!publicKey || !connectId) {
		return;
	}

	const stripe = await loadStripe(publicKey, {stripeAccount: connectId, locale: 'auto'});
	if (!stripe) {
		return;
	}

	const elements = stripe.elements();

	const payments: AbstractStripePayment[] = [];

	if (Feature.enabled(FeatureFlag.STRIPE_CARD_GATEWAY)) {
		payments.push(new StripeCardPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_BANCONTACT_GATEWAY)) {
		payments.push(new StripeBancontactPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_GIROPAY_GATEWAY)) {
		payments.push(new StripeGiropayPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_AFFIRM_GATEWAY)) {
		payments.push(new StripeAffirmPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_SOFORT_GATEWAY)) {
		payments.push(new StripeSofortPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_EPS_GATEWAY)) {
		payments.push(new StripeEpsPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_P24_GATEWAY)) {
		payments.push(new StripeP24Payment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_SEPADEBIT_GATEWAY)) {
		payments.push(new StripeSepaDebitPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_KLARNA_GATEWAY)) {
		payments.push(new StripeKlarnaPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_AFTERPAY_GATEWAY)) {
		payments.push(new StripeAfterPayPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_IDEAL_GATEWAY)) {
		payments.push(new StripeIdealPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_GOOGLEPAY_GATEWAY)) {
		payments.push(new StripeGooglepayPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_APPLEPAY_GATEWAY)) {
		payments.push(new StripeApplepayPayment(stripe, elements));
	}

	if (Feature.enabled(FeatureFlag.STRIPE_ACHDEBIT_GATEWAY)) {
		payments.push(new StripeAchdebitPayment(stripe, elements));
	}

	await Promise.all(payments.map(p => p.init()));

	onCheckoutUpdate(() => {
		$qs('#payment_method_peachpay_stripe_card', $el => {
			if (!$el.parentElement) {
				return;
			}

			const paymentSelector = $el.parentElement.querySelector('.payment_method_peachpay_stripe_card #pp-stripe-card-status');
			if (!paymentSelector) {
				return;
			}

			const $form = document.createElement('form');
			$form.setAttribute('id', 'pp-stripe-form-submit');
			$form.style.margin = '0';
			paymentSelector.before($form);
		});

		$qs('#pp-stripe-card-element', $el => {
			$qs('#pp-stripe-form-submit', $form => {
				$form.append($el);
			});
		});
	});

	onCheckoutSubmitManual(
		$checkoutForm,
		payments.map(v => v.gatewayId()),
		async gatewayId => {
			let transactionId: string | null = null;
			const payment = payments.find(v => v.gatewayId() === gatewayId);
			if (!payment) {
				return false;
			}

			try {
				cleanupTransactionFields();
				cleanupTokenFields();
				cleanupCaptchaFields();

				if (tokenizedMethodSelected(gatewayId)) {
					transactionId = await createPaymentTransaction(gatewayId);
					if (!transactionId) {
						return false;
					}
				} else {
					const [paymentId, update] = await payment.tokenize();
					if (!paymentId && update?.note) {
						payment.displayPaymentError(update.note);
					}

					transactionId = await createPaymentTransaction(gatewayId);
					if (!transactionId) {
						return false;
					}

					if (!paymentId) {
						await updatePaymentTransaction(transactionId, update);
						return false;
					}

					insertTokenFields($checkoutForm, paymentId);
				}

				insertTransactionFields($checkoutForm, transactionId);
				await insertCaptchaFields($checkoutForm);

				return true;
			} catch (error) {
				if (transactionId) {
					await updatePaymentTransaction(transactionId, {
						note: getErrorString(error),
						paymentStatus: 'creationfailed',
					});
				}

				payment.displayPaymentError((error as Error).message);

				unblockCheckout($checkoutForm);
				return false;
			}
		});

	onCheckoutSubmitPaymentData<StripePaymentData>('stripe', async data => {
		if (data.type !== 'stripe') {
			return;
		}

		const payment = payments.find(v => data.gateway === v.gatewayId());
		if (!payment) {
			return;
		}

		try {
			const [status, update] = await payment.confirm(data.data.client_secret, data.intermediate_url);

			if (!status) {
				await updatePaymentTransaction(data.transaction_id, {
					note: update?.note ?? 'An error occured while processing the payment',
					paymentStatus: 'failed',
				});

				if (update?.note) {
					payment.displayPaymentError(update.note);
					unblockCheckout($checkoutForm);
				}

				return;
			}

			if (status === 'success') {
				window.location.href = data.success_url;
			}
		} catch (error) {
			await updatePaymentTransaction(data.transaction_id, {
				note: getErrorString(error),
				paymentStatus: 'failed',
			});

			payment.displayPaymentError((error as Error).message);
			unblockCheckout($checkoutForm);
		}
	});

	onCheckoutSubmitErrorManual($checkoutForm, payments.map(v => v.gatewayId()), async (transactionId, errors) => {
		cleanupTokenFields();
		cleanupTransactionFields();
		cleanupCaptchaFields();

		if (!transactionId) {
			return;
		}

		try {
			await updatePaymentTransaction(transactionId, {
				paymentStatus: 'unperformed',
				orderStatus: 'creationfailed',
				note: errors.join('\n'),
			});
		} catch {
			// Do no harm.
		}
	});
}

function insertTokenFields($checkoutForm: HTMLFormElement, paymentId?: string) {
	if (paymentId) {
		const $sourceId = document.createElement('input');
		$sourceId.type = 'hidden';
		$sourceId.name = 'peachpay_stripe_payment_method_id';
		$sourceId.value = paymentId;
		$checkoutForm.insertAdjacentElement('beforeend', $sourceId);
	}
}

function cleanupTokenFields() {
	$qsAll('input[name="peachpay_stripe_payment_method_id"]', $el => {
		$el.remove();
	});
}

function billingDetails(): PaymentMethodCreateParams.BillingDetails {
	if (!FormFields.billing.firstName.get()) {
		return {};
	}

	const details = {
		name: `${FormFields.billing.firstName.get()} ${FormFields.billing.lastName.get()}`,
		email: FormFields.billing.email.get(),
		phone: FormFields.billing.phone.get(),
		address: {
			city: FormFields.billing.city.get(),
			country: FormFields.billing.country.get(),
			line1: FormFields.billing.address1.get(),
			line2: FormFields.billing.address2.get(),
			postal_code: FormFields.billing.postcode.get(),
			state: FormFields.billing.state.get(),
		},
	} as PaymentMethodCreateParams.BillingDetails;

	if (details.name === ' ') {
		delete details.name;
	}

	if (details.email === '') {
		delete details.email;
	}

	if (details.phone === '') {
		delete details.phone;
	}

	if (details.address?.city === '') {
		delete details.address.city;
	}

	if (details.address?.country === '') {
		delete details.address.country;
	}

	if (details.address?.line1 === '') {
		delete details.address.line1;
	}

	if (details.address?.line2 === '') {
		delete details.address.line2;
	}

	if (details.address?.postal_code === '') {
		delete details.address.postal_code;
	}

	if (details.address?.state === '') {
		delete details.address.state;
	}

	if (Object.keys(details.address ?? {}).length === 0) {
		delete details.address;
	}

	return details;
}

export {
	initStripePayments,
	billingDetails,
	insertTokenFields,
};
